import { TICK_DT } from '../physics/constants';

/**
 * Fixed-timestep accumulator with render interpolation.
 *
 * The sim must advance in identical increments regardless of display rate: a
 * 144 Hz monitor and a 60 Hz one have to produce the same lap time, and ghosts
 * and replay validation both assume a fixed tick.
 */
export class GameLoop {
  private accumulator = 0;
  private lastTime = 0;
  private rafId = 0;
  private running = false;

  constructor(
    private readonly onTick: () => void,
    private readonly onRender: (alpha: number, frameDt: number) => void,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  private frame = (now: number): void => {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.frame);

    // Cap the delta. A backgrounded tab returns a delta of many seconds, and
    // without this the loop would try to catch up and lock the page solid.
    const frameDt = Math.min((now - this.lastTime) / 1000, 0.25);
    this.lastTime = now;
    this.accumulator += frameDt;

    let ticks = 0;
    while (this.accumulator >= TICK_DT && ticks < 240) {
      this.onTick();
      this.accumulator -= TICK_DT;
      ticks++;
    }

    this.onRender(this.accumulator / TICK_DT, frameDt);
  };
}
